import {
  useCreateProductFeatureMutation,
  useDeleteProductFeatureMutation,
} from "api/memberPortal/features/productFeatures/productFeatures.api";
import { useRemoveFileMutation } from "api/memberPortal/files/files.api";
import { useUploadFrontendFileMutation } from "api/public/file/file.api";
import {
  useCreatePcmMutation,
  useUpdatePcmMutation,
} from "api/public/pcm/pcm.api";
import {
  MaintenanceCC,
  PcmCreatePayload,
  PcmUpdatePayload,
  ProductCC,
} from "api/public/pcm/pcm.types";
import { Scheme } from "api/public/schemes/schemes.types";
import { formatFormBodyDate, getISOCurrentDate } from "utils/timeUtils";

import { PcmFileMetadata } from "./sections/PcmFileUploadSection";

import type { PcmFormValues } from "./ProposeInternationalProduct.schema";
import type { FeatureData } from "components/Forms/components/sections/TestedFeaturesSection/TestedFeaturesSection";

interface BuildPcmPayloadProps {
  value: PcmFormValues;
  recaptchaToken: string;
  product: ProductCC;
  scheme: Scheme;
  ccMaintenance?: MaintenanceCC;
}

export const usePcmFormMutations = () => {
  const [triggerCreatePcm] = useCreatePcmMutation();
  const [triggerUpdatePcm] = useUpdatePcmMutation();
  const [triggerFileUpload] = useUploadFrontendFileMutation();
  const [triggerRemoveFile] = useRemoveFileMutation();
  const [triggerCreateFeature] = useCreateProductFeatureMutation();
  const [triggerDeleteFeature] = useDeleteProductFeatureMutation();

  const buildPcmPayload = ({
    value,
    recaptchaToken,
    product,
    scheme,
    ccMaintenance,
  }: BuildPcmPayloadProps): PcmCreatePayload => {
    const certifiedDate = product?.certified ?? null;
    // Calculate default deadline (30 days from now) if not provided
    const defaultDeadline = new Date();
    defaultDeadline.setDate(defaultDeadline.getDate() + 30);

    const payload: PcmCreatePayload = {
      prod: product?.name ?? null,
      categories: product?.categories ?? "",
      pdf_cert: product?.pdf_cert ?? "",
      pdf_st: product?.pdf_st ?? "",
      ccid: product?.ccid ?? null,
      nsid: scheme?.sid ? { sid: scheme.sid } : null,
      lab: typeof value.ccLab?.value === "number" ? value.ccLab.value : null,
      recaptcha_token: recaptchaToken,
      certified: certifiedDate,
      am_date: null,
      vendor_name: product?.vendor_name ?? null,
      entrydate: getISOCurrentDate(),
      rcvd: getISOCurrentDate(),
      deadline: value.deadline
        ? formatFormBodyDate(value.deadline)
        : formatFormBodyDate(defaultDeadline),
      phone1: value.phone1,
      custemail: value.custemail,
      custuname: value.custuname,
      additional_information: value.additionalInformation,
      protection_profile: value.protectionProfiles
        .map((pp) => (typeof pp.value === "number" ? pp.value : null))
        .filter((v): v is number => v !== null),
    };

    // Add maintenance fields if applicable
    if (value.isMaintenance && ccMaintenance) {
      payload.add_maintenance = true;
      payload.maintenance_id = ccMaintenance.maintenance_id;
      payload.prod = ccMaintenance.name;
    }

    return payload;
  };

  interface BuildUpdatePcmPayloadProps extends BuildPcmPayloadProps {
    pcmRid: number;
  }
  const buildUpdatePayload = ({
    value,
    pcmRid,
    recaptchaToken,
    product,
    scheme,
  }: BuildUpdatePcmPayloadProps): PcmUpdatePayload => {
    const basePayload = buildPcmPayload({
      value,
      recaptchaToken,
      product,
      scheme,
    });

    return {
      ...basePayload,
      existing_pcm: pcmRid,
      update: value.update,
      send_response: value.send_response,
    };
  };

  const handleFileUpload = (
    fileTypeId: number,
    files: PcmFileMetadata[] | undefined,
  ) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      // Remove files that were marked as inactive
      if (file.file_id && file.active === false) {
        triggerRemoveFile(file.file_id);
      }
      // Upload new files (no file_id means it's a new file)
      else if (!file.file_id && file.file) {
        triggerFileUpload({
          file: file.file,
          file_type_id: fileTypeId,
          file_type: "ccp",
          file_display_name: file.fileType || "",
          file_name: file.fileName || "",
          file_label: file.fileLabel || "",
          active: true,
          uploaded_on: getISOCurrentDate(),
        });
      }
    }
  };

  /**
   * Handle feature changes - create new features and delete removed ones
   */
  const handleFeatureChanges = (
    originalFeatureIds: number[],
    currentFeatures: FeatureData[],
    pcmRid: number,
  ) => {
    // Find features to delete (in original but not in current)
    const currentFeatureIds = currentFeatures
      .filter((f) => f.id !== undefined)
      .map((f) => f.id as number);

    const featuresToDelete = originalFeatureIds.filter(
      (id) => !currentFeatureIds.includes(id),
    );

    // Delete removed features
    for (const featureId of featuresToDelete) {
      triggerDeleteFeature({ id: featureId, pcm: pcmRid });
    }

    // Create new features (those without an id)
    const newFeatures = currentFeatures.filter((f) => f.id === undefined);
    for (const feature of newFeatures) {
      triggerCreateFeature({
        pcm: pcmRid,
        feature_name: feature.feature_name,
      });
    }
  };

  return {
    triggerCreatePcm,
    triggerUpdatePcm,
    buildPcmPayload,
    buildUpdatePayload,
    handleFileUpload,
    handleFeatureChanges,
  };
};
